import { t, useSubject } from '@base/utils/helpers';
import { MenuItem, Select } from '@material-ui/core';
import MainButton from '@ui-kit/components/mainButton';
import { useController, useForm } from 'react-hook-form';
import { container } from 'tsyringe';
import { IUserService } from '../user/model/interfaces';
import styles from './contactUs.module.scss';
import { IHelpService, IssueInfo } from '../help/model/interfaces';
import { Loader } from '@ui-kit/components/loader';
import { useState, useEffect } from 'react';
import { ResponseCode } from '@base/utils/constants';
import { IModalProvider } from '@base/features/modal/model/interfaces';
import { useAsyncData } from '@utils/hooks';

function ContactUs() {
  const modalProvider: IModalProvider = container.resolve('modalProvider');
  const helpService: IHelpService = container.resolve('helpService');
  const userService: IUserService = container.resolve('userService');
  const { data: personalInfo } = useAsyncData(() => userService.getUserInfo());

  const [issueInfo, setIssueInfo] = useState<IssueInfo>();
  const [categories, setCategories] = useState({
    '0': '-- Please select the topic of the issue --',
  });

  const info = {
    name: personalInfo ? personalInfo.real_name.first_name : '',
    login: personalInfo ? personalInfo.login_name : '',
    email: personalInfo ? personalInfo.email : '',
  };

  const { control, handleSubmit, formState, register, trigger } = useForm({
    mode: 'all',
    defaultValues: {
      category: '0',
      issue_number: '',
      issue_text: '',
      ...info,
    },
  });

  useEffect(() => {
    (async function () {
      try {
        const issue = await helpService.getIssueInfo();
        setIssueInfo(issue);
        setCategories({ ...categories, ...issue.categories });
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const { field: topicSelect } = useController({
    name: 'category',
    control,
    rules: {
      required: true,
      validate: (v) => Object.prototype.hasOwnProperty.call(issueInfo?.categories, v),
    },
  });

  const contact = handleSubmit(async (data) => {
    if (!issueInfo) return;

    helpService
      .contactUsWithParams(
        issueInfo.key,
        data.name,
        data.email || '',
        data.category,
        data.issue_text,
        data.issue_number
      )
      .then((res) => {
        if (res.code === ResponseCode.SUCCESS) {
          modalProvider.show({
            title: t('success'),
            body: t('request_successfully_submitted'),
            buttons: [{ title: t('ok'), onClick: () => modalProvider.hide() }],
          });
        } else {
          console.log(res.code);
        }
      })
      .catch(() => {
        modalProvider.show({
          title: t('error'),
          body: t('cannot_connect_to_server'),
          buttons: [{ title: t('ok'), onClick: () => modalProvider.hide() }],
        });
      });
  });

  if (!issueInfo) return <Loader width={150} height={150} />;

  return (
    <div className={styles['contact-us']}>
      <h3>You can also contact us with any issues or comments</h3>
      <form className={styles.form} onSubmit={contact}>
        <div className={styles['contact-input']}>
          <input
            id="name"
            type="text"
            {...register('name', { required: true })}
            placeholder={t('screen_name')}
          />
          <span></span>
        </div>
        <div className={styles['contact-input']}>
          <input id="login" type="text" {...register('login')} placeholder={t('name')} />
          <span></span>
        </div>
        <div className={styles['contact-input']}>
          <input
            id="email"
            type="email"
            {...register('email', { required: true })}
            placeholder={t('email')}
          />
        </div>
        <div className={styles['contact-input']}>
          <input
            id="issue_number"
            type="text"
            {...register('issue_number')}
            placeholder={t('issue_number')}
          />
          <span>{t('leave_blank_if_none')}</span>
        </div>

        <div className={styles.select}>
          <Select {...topicSelect}>
            {Object.entries(categories).map(([value, category], i) => (
              <MenuItem key={value} disabled={!i} value={value}>
                <span className={!i ? styles.placeholder : ' '}>{category}</span>
              </MenuItem>
            ))}
          </Select>
        </div>

        <div className={styles['contact-textarea']}>
          <textarea
            id="issue_text"
            {...register('issue_text', { required: true })}
            rows={4}
            placeholder={t('fill_in_your_comment')}
          />
          <span>Please use English only.</span>
        </div>
        <div className={styles.button}>
          <MainButton disabled={!formState.isValid} type="submit">
            {t('submit')}
          </MainButton>
        </div>
      </form>
      <div className={styles.terms}>
        <p>We will do our best to answer all inquiries within 24 hours or less.</p>
        <p>
          For $Ticket holders -- within 8 hours or less. $Ticket Game disputes must be initiated within 6
          hours.
        </p>
        <p>
          $Ticket Game disputes are resolved fairly and impartially as per{' '}
          <a target="_blank" rel="noopener noreferrer" href="https://www.gamecolony.com/gtc99.html">
            TERMS AND CONDITIONS
          </a>
          .
        </p>
      </div>
    </div>
  );
}

export default ContactUs;
